import {
  CreateQuoteDocument,
  QuoteDocumentConfig,
  QuoteDocumentConfigPositionEnum,
} from '@monetize/types/app';
import { MimeTypeEnum } from '@monetize/types/common';
import { useEffect, useState } from 'react';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  doGetQuoteDocumentConfig,
  useDeleteDocumentConfig,
  useUpdateQuoteDocumentConfigurations,
} from '../../../../../api/legalEntityService';
import { enqueueForQuote } from '../../../../../api/quoteWriteChain';
import {
  useGetQuoteDocumentTypes,
  useGetTenantQuoteDocumentDefinitions,
  useUploadTenantQuoteDocument,
} from '../../../../../api/settingsService';
import {
  useQuoteDocumentUploadNavigationGuard,
  useQuoteDocumentUploadTabCloseGuard,
} from '../../../../../hooks/useQuoteDocumentUploads';
import { base64ToArrayBuffer } from '../../../../../utils';
import {
  downloadBlobAsFile,
  showPdfInNewTab,
} from '../../../../../utils/download';
import { QuoteDocumentsMode } from '../../../../Quotes/QuoteV2/components/QuoteDocumentsList/constants';
import { QuoteDocumentsList } from '../../../../Quotes/QuoteV2/components/QuoteDocumentsList/QuoteDocumentsList';
import {
  buildDocumentUploadFormData,
  buildSingleDocumentUpdate,
  orchestrateDocumentReplace,
} from '../../../../Quotes/QuoteV2/components/quoteDocumentsListUtils';

// There's only ever one tenant document-definitions list per session (unlike
// quotes or legal entities, which each need their own id) — a fixed key is
// enough to scope its pending-upload state, and avoids the id briefly being
// empty before useGetTenantQuoteDocumentDefinitions resolves.
const TENANT_QUOTE_DOCUMENTS_OWNER_ID = 'tenant-quote-documents';

export const TenantQuoteDocumentConfigurations = () => {
  useQuoteDocumentUploadNavigationGuard(TENANT_QUOTE_DOCUMENTS_OWNER_ID);
  useQuoteDocumentUploadTabCloseGuard(TENANT_QUOTE_DOCUMENTS_OWNER_ID);

  const [beforeDocuments, setBeforeDocuments] = useState<QuoteDocumentConfig[]>(
    [],
  );
  const [afterDocuments, setAfterDocuments] = useState<QuoteDocumentConfig[]>(
    [],
  );

  const { data: quoteDocumentTypes } = useGetQuoteDocumentTypes();

  const { data: quoteDocumentDefinitions, refetch } =
    useGetTenantQuoteDocumentDefinitions();

  const { mutateAsync: deleteDocumentConfig } = useDeleteDocumentConfig();

  const {
    mutate: updateQuoteDocumentConfigurations,
    mutateAsync: updateQuoteDocumentConfigurationsAsync,
  } = useUpdateQuoteDocumentConfigurations(quoteDocumentDefinitions?.id ?? '');

  const { mutateAsync: uploadQuoteDocument } = useUploadTenantQuoteDocument();

  useEffect(() => {
    if (quoteDocumentDefinitions?.configurations) {
      const before = quoteDocumentDefinitions.configurations
        .filter(
          (doc) => doc.position === QuoteDocumentConfigPositionEnum.BEFORE,
        )
        .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
      const after = quoteDocumentDefinitions.configurations
        .filter((doc) => doc.position === QuoteDocumentConfigPositionEnum.AFTER)
        .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition);
      setBeforeDocuments(before);
      setAfterDocuments(after);
    }
  }, [quoteDocumentDefinitions]);

  const handleOpenDocument = async (
    documentConfigId: string,
    filename?: string,
  ) => {
    if (!quoteDocumentDefinitions?.id) {
      return;
    }

    try {
      const documentConfig = await doGetQuoteDocumentConfig(
        quoteDocumentDefinitions.id,
        documentConfigId,
        true,
      );

      if (documentConfig.storageObject.contentBase64Encoded && filename) {
        showPdfInNewTab(
          documentConfig.storageObject.contentBase64Encoded,
          filename,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleDownloadDocument = async (doc: QuoteDocumentConfig) => {
    if (!quoteDocumentDefinitions?.id) {
      return;
    }

    try {
      const documentConfig = await doGetQuoteDocumentConfig(
        quoteDocumentDefinitions.id,
        doc.id,
        true,
        doc.storageObject.mediaType as MimeTypeEnum,
      );

      if (documentConfig.storageObject.contentBase64Encoded) {
        downloadBlobAsFile(
          base64ToArrayBuffer(
            documentConfig.storageObject.contentBase64Encoded,
          ),
          documentConfig.storageObject.filename,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleDeleteDocument = async (documentConfigId: string) => {
    if (!quoteDocumentDefinitions?.id) {
      return;
    }

    await deleteDocumentConfig({
      documentDefinitionId: quoteDocumentDefinitions.id,
      documentConfigId,
    });
  };

  // Serializing per owner prevents concurrent uploads' refetch() calls from
  // racing — React Query cancels an in-flight refetch when another one
  // starts and resolves the cancelled call with stale cached data, which
  // can clear a pending upload's placeholder before its row has actually
  // landed. Mirrors AttachedQuoteDocuments.tsx's use of enqueueForQuote for
  // the same reason.
  const submitDocumentFile = (data: CreateQuoteDocument, file: File) =>
    enqueueForQuote(TENANT_QUOTE_DOCUMENTS_OWNER_ID, async () => {
      await uploadQuoteDocument(buildDocumentUploadFormData(data, file));
      const { data: refreshed } = await refetch();
      return refreshed?.configurations;
    });

  const handleCreateDocument = async (
    data: CreateQuoteDocument,
    file: File,
  ) => {
    await submitDocumentFile(data, file);
  };

  const handleReplaceDocument = async (
    doc: QuoteDocumentConfig,
    file: File,
  ) => {
    if (!quoteDocumentDefinitions?.id) {
      return;
    }
    const documentDefinitionId = quoteDocumentDefinitions.id;

    try {
      await orchestrateDocumentReplace(doc, file, {
        beforeDocuments,
        afterDocuments,
        deleteDocument: (documentConfigId) =>
          deleteDocumentConfig({ documentDefinitionId, documentConfigId }),
        createDocument: submitDocumentFile,
        persistReorder: updateQuoteDocumentConfigurationsAsync,
      });
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleChangeDocumentType = async (
    doc: QuoteDocumentConfig,
    documentTypeId: string,
  ) => {
    try {
      await updateQuoteDocumentConfigurationsAsync(
        buildSingleDocumentUpdate(doc, { documentTypeId }),
      );
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const handleToggleAutoAdd = async (
    doc: QuoteDocumentConfig,
    autoAddToQuotes: boolean,
  ) => {
    try {
      await updateQuoteDocumentConfigurationsAsync(
        buildSingleDocumentUpdate(doc, { autoAddToQuotes }),
      );
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  return (
    <QuoteDocumentsList
      mode={QuoteDocumentsMode.SETTINGS}
      ownerId={TENANT_QUOTE_DOCUMENTS_OWNER_ID}
      isReadOnly={false}
      beforeDocuments={beforeDocuments}
      afterDocuments={afterDocuments}
      quoteDocumentTypes={quoteDocumentTypes ?? []}
      onOpenDocument={handleOpenDocument}
      onDownloadDocument={handleDownloadDocument}
      onDeleteDocument={handleDeleteDocument}
      onChangeDocumentType={handleChangeDocumentType}
      onReplaceDocument={handleReplaceDocument}
      onCreateDocument={handleCreateDocument}
      onUpdateDocumentConfigurations={updateQuoteDocumentConfigurations}
      onToggleAutoAdd={handleToggleAutoAdd}
    />
  );
};
